/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { Button } from '@mui/material'
import { createRef, useEffect, useMemo, useState, useCallback } from 'react'
import ReactAudioPlayer, { RHAP_UI } from 'react-h5-audio-player'
import 'react-h5-audio-player/lib/styles.css'
import { ButtonMenu } from '..'

const MobileBreakpoint = 768

export interface AudioPlayerProps {
  crossOrigin?: string | undefined
  src: string
  onError?: any
}

export default function AudioPlayer({ crossOrigin, src, onError }: AudioPlayerProps): JSX.Element {
  const player = createRef<ReactAudioPlayer>()
  const [playbackRate, setPlaybackRate] = useState<number>(1)
  const isOnMobile = useIsOnMobile()

  useEffect(() => {
    player.current!.audio.current!.playbackRate = playbackRate
  }, [playbackRate])

  const PlaybackRateOptions = useMemo(() => {
    return (
      <ButtonMenu
        key="plaback-rate"
        color="primary"
        id="playback-rate-options"
        buttonRenderer={<Button>{`${playbackRate}x`}</Button>}
        menuItems={[
          {
            content: '0.75x',
            onClick: () => setPlaybackRate(0.75),
            type: 'MenuItem',
          },
          {
            content: '1x',
            onClick: () => setPlaybackRate(1),
            type: 'MenuItem',
          },
          {
            content: '1.5x',
            onClick: () => setPlaybackRate(1.5),
            type: 'MenuItem',
          },
          {
            content: '2x',
            onClick: () => setPlaybackRate(2),
            type: 'MenuItem',
          },
        ]}
      />
    )
  }, [playbackRate])

  return (
    <div
      css={(theme: any) => css`
        .rhap_main-controls-button {
          color: ${theme?.palette?.primary?.main || 'hsl(321, 53%, 31%)'};
        }
        .rhap_progress-indicator {
          background: ${theme?.palette?.primary?.main || 'hsl(321, 53%, 31%)'};
        }
        .rhap_additional-controls {
          flex: 1;
        }
      `}
    >
      <ReactAudioPlayer
        // @ts-ignore
        crossOrigin={crossOrigin}
        ref={player}
        src={src}
        autoPlay={false}
        autoPlayAfterSrcChange={false}
        showFilledProgress
        progressJumpSteps={{ backward: 10000, forward: 10000 }}
        customAdditionalControls={isOnMobile ? [PlaybackRateOptions] : [RHAP_UI.LOOP, PlaybackRateOptions]}
        customVolumeControls={isOnMobile ? [] : [RHAP_UI.VOLUME]}
        customControlsSection={
          isIOS ? [] : [RHAP_UI.ADDITIONAL_CONTROLS, RHAP_UI.MAIN_CONTROLS, RHAP_UI.VOLUME_CONTROLS]
        }
        onError={onError}
      />
    </div>
  )
}

function useIsOnMobile() {
  const [isOnMobile, setIsOnMobile] = useState<boolean>(window.innerWidth < MobileBreakpoint)
  const handleResize = useCallback(() => {
    if (!isOnMobile && window.innerWidth < MobileBreakpoint) {
      setIsOnMobile(true)
    } else if (isOnMobile && window.innerWidth >= MobileBreakpoint) {
      setIsOnMobile(false)
    }
  }, [isOnMobile])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [isOnMobile])

  return isOnMobile
}

// TODO: use navigator.userAgentData.platform since navigator.platform is deprecated
const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
